import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '@/assets/images/logo.webp';
import { Menu, X, ChevronDown, ChevronRight } from 'lucide-react';
// import EnquiryModal from './contact-us/EnquiryModal';
import FloatingActionButton from '@/components/FloatingActionButton';
//import EnquiryModal from './contact-us/EnquiryModal';
import EnquiryModalPopup from '@/components/EnquiryModalPopup';

const isComingSoon = (project) => project.slug?.toLowerCase() === 'sarvam';

// Opens the given URL exactly as-is, bypassing Google Analytics/Ads
// cross-domain "linker" auto-tagging (which rewrites <a href> on click to
// append ?_gl=...&_gcl_au=... for domains configured in GA4). Since we open
// the original JS string via window.open instead of reading the (possibly
// GA-mutated) DOM href attribute, the destination URL always stays clean.
const openExternalClean = (e, url) => {
  e.preventDefault();
  window.open(url, '_blank', 'noopener,noreferrer');
};
const PROJECTS_CACHE_KEY = 'kgk_projects_cache_v1';

// === Desktop mega menu helpers ===
const DesktopProjectLink = ({ project }) => {
  if (isComingSoon(project)) {
    return (
      <span className="text-sm text-gray-400 cursor-not-allowed block py-0.5">
        {project.name}
      </span>
    );
  }
  return (
    <Link
      to={`/project-details/${project.slug}`}
      className="text-sm text-gray-600 hover:text-secondary block py-0.5 transition-colors"
    >
      {project.name}
    </Link>
  );
};

const DesktopProjectList = ({ projects, loading, emptyText = 'Coming soon' }) => (
  <ul className="space-y-1.5 max-h-52 overflow-y-auto pr-1 mega-menu-scroll">
    {projects.length ? (
      projects.map((p) => (
        <li key={p.slug}>
          <DesktopProjectLink project={p} />
        </li>
      ))
    ) : loading ? null : (
      <li className="text-xs text-gray-400 italic">{emptyText}</li>
    )}
  </ul>
);

const DesktopColumnHeading = ({ title, url }) => (
  <Link
    to={url}
    className="group/col flex items-center justify-between text-base font-bold text-gray-900 hover:text-secondary pb-3 mb-4 border-b-2 border-secondary/40 transition-colors"
  >
    {title}
    <ChevronRight size={16} className="group-hover/col:translate-x-1 transition-transform" />
  </Link>
);

// === Mobile accordion helpers ===
const MobileProjectLink = ({ project, onNavigate }) => {
  if (isComingSoon(project)) {
    return (
      <span className="py-[10px] pl-8 border-b border-white/10 text-xs text-white/40 italic block">
        {project.name} (Coming Soon)
      </span>
    );
  }
  return (
    <Link
      to={`/project-details/${project.slug}`}
      onClick={onNavigate}
      className="py-[10px] pl-8 border-b border-white/10 text-xs text-white/80 block"
    >
      {project.name}
    </Link>
  );
};

const MobileProjectList = ({ projects, onNavigate, loading, emptyText = 'Coming soon' }) =>
  projects.length ? (
    projects.map((p) => <MobileProjectLink key={p.slug} project={p} onNavigate={onNavigate} />)
  ) : loading ? null : (
    <span className="py-[10px] pl-8 text-xs text-white/40 italic block">{emptyText}</span>
  );

const MobileAccordionRow = ({ label, url, depth = 0, open, onToggle, onNavigate, children }) => (
  <>
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className={`w-full flex items-center justify-between gap-2 border-b border-white/20 text-white text-left uppercase ${
        depth === 0 ? 'py-[14px] pl-4 pr-3' : 'py-[12px] pl-6 pr-3 text-sm'
      }`}
    >
      <span>{label}</span>
      <ChevronDown
        className={`shrink-0 ${open ? 'rotate-180' : ''} transition-transform duration-300`}
        size={depth === 0 ? 16 : 14}
      />
    </button>
    {/* Plain conditional render (no animated max-height/overflow wrapper).
        Nested accordions here can go 3 levels deep (Projects > Residential >
        Jaipur), and stacking that many animated max-height/overflow-hidden
        transitions is a known source of browsers silently mis-painting
        content (correct layout, invisible text) once settled. Conditional
        rendering has no such failure mode. */}
    {open && (
      <div className="flex flex-col">
        <Link
          to={url}
          onClick={onNavigate}
          className={`block border-b border-white/10 text-secondary font-semibold ${
            depth === 0 ? 'py-[10px] pl-6 text-sm' : 'py-[8px] pl-8 text-xs'
          }`}
        >
          View All {label} →
        </Link>
        {children}
      </div>
    )}
  </>
);

const Navbar = () => {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(80);
  const [modalOpen, setModalOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [employeeOpen, setEmployeeOpen] = useState(false);

  // Mobile "Projects" accordion state
  const [projectOpen, setProjectOpen] = useState(false);
  const [residentialOpen, setResidentialOpen] = useState(false);
  const [jaipurOpen, setJaipurOpen] = useState(false);
  const [mumbaiOpen, setMumbaiOpen] = useState(false);
  const [commercialOpen, setCommercialOpen] = useState(false);
  const [industrialOpen, setIndustrialOpen] = useState(false);

  // Cached across route changes (Navbar remounts on every page via MainLayout)
  // so the mega menu shows instantly after the first slow fetch instead of
  // re-showing an empty list on every navigation.
  const [allProjects, setAllProjects] = useState(() => {
    try {
      const cached = sessionStorage.getItem(PROJECTS_CACHE_KEY);
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });
  const [projectsLoading, setProjectsLoading] = useState(() => {
    try {
      return !sessionStorage.getItem(PROJECTS_CACHE_KEY);
    } catch {
      return true;
    }
  });

  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    propertyType: '',
    location: '',
    message: '',
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const propertyTypes = ['Residential', 'Commercial', 'Industrial'];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePropertyTypeClick = (type) => {
    setFormData({ ...formData, propertyType: type });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.fullName || !formData.phone || !formData.email || !formData.propertyType) {
      setError('Please fill all required fields.');
      setSuccess('');
      return;
    }

    const payload = {
      full_name: formData.fullName,
      email: formData.email,
      phone: formData.phone,
      property_type: formData.propertyType,
      location: formData.location,
      message: formData.message,
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setSuccess('Your enquiry has been submitted!');
        setError('');
        setFormData({
          fullName: '',
          phone: '',
          email: '',
          propertyType: '',
          location: '',
          message: '',
        });
        setTimeout(() => setModalOpen(false), 1500);
      } else {
        const errorData = await response.json();
        setError(errorData?.message || 'Something went wrong.');
        setSuccess('');
      }
    } catch (err) {
      setError('Failed to submit. Please try again later.');
      setSuccess('');
      console.error(err);
    }
  };

  const loginMenu = [
    { id: 'customer', label: 'Customer', url: 'https://server16.farvisioncloud.com/login' },
    {
      id: 'employee',
      label: 'Employee',
      subItems: [
        { id: 'employee-email', label: 'Email', url: 'https://mail.kgkrealty.com' },
        { id: 'employee-farvision', label: 'Farvision', url: 'https://server16.farvisioncloud.com/login' },
      ],
    },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setScrolled(window.scrollY > 50);
  }, [location.pathname]);

  // Fetch all projects for the Projects mega menu. Runs on every mount (this
  // component remounts per page), but the cache above already showed last
  // known data instantly — this just refreshes it silently in the background.
  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.REACT_APP_API_BASE_URL}/projects`)
      .then((res) => res.json())
      .then((data) => {
        const list = data?.projects || [];
        if (!cancelled) {
          setAllProjects(list);
          try {
            sessionStorage.setItem(PROJECTS_CACHE_KEY, JSON.stringify(list));
          } catch {
            // sessionStorage unavailable (e.g. private mode) — fine, just no caching
          }
        }
      })
      .catch((err) => console.error('Error fetching projects for menu:', err))
      .finally(() => {
        if (!cancelled) setProjectsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const byTypeCity = (type, city) =>
    allProjects.filter(
      (p) => p.type?.toLowerCase() === type && p.city?.toLowerCase() === city
    );
  const byType = (type) => allProjects.filter((p) => p.type?.toLowerCase() === type);

  const residentialJaipur = byTypeCity('residential', 'jaipur');
  const residentialMumbai = byTypeCity('residential', 'mumbai');
  const commercialProjects = byType('commercial');
  const industrialProjects = byType('industrial');

  // Measure the real header height instead of guessing, so the mobile
  // drawer always lines up exactly below it (scrolled or not).
  useEffect(() => {
    if (!headerRef.current) return;
    const el = headerRef.current;
    const update = () => setHeaderHeight(el.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Lock background scroll while the mobile drawer is open so the header
  // (and therefore the drawer's position) can't shift underneath it.
  useEffect(() => {
    if (!isOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [isOpen]);

  const closeMobileMenu = () => setIsOpen(false);

  const isActive = (id) => {
    const path = location.pathname.toLowerCase();
    switch (id) {
      case 'about': return path.includes('/about-us');
      case 'connect': return path.includes('/contact-us');
      case 'residential': return path.startsWith('/residential-projects');
      case 'residential-jaipur': return path.includes('/residential-projects/jaipur');
      case 'residential-mumbai': return path.includes('/residential-projects/mumbai');
      case 'commercial': return path.startsWith('/commercial-project');
      case 'luxury':
      case 'industrial': return path.startsWith('/industrial-project');
      case 'project':
        return (
          path.startsWith('/residential-projects') ||
          path.startsWith('/commercial-project') ||
          path.startsWith('/industrial-project') ||
          path.startsWith('/project-details')
        );
      default: return false;
    }
  };

  return (
    <>
      <header
        ref={headerRef}
        className={`fixed w-full z-50 transition-all duration-300`}
        // className={`fixed w-full z-50 transition-all duration-300 ${scrolled
        //   ? 'bg-gradient-to-r from-[#956325] to-[#c97a17]'
        //   : 'bg-main-holder bg-main-mobile'
        //   }`}
      >
        <div className="container-fluid py-2 flex items-center justify-between">
          <Link to="/">
            <img src={logo} alt="KGK Realty" className={`transition-all duration-300 ${scrolled ? 'h-16' : 'lg:h-16 h-16'}`} />
          </Link>
          <div className="hidden md:flex items-center gap-16 relative">
            <nav className="flex items-center space-x-10 text-white font-medium uppercase text-lg tracking-wider">
              <a href="/about-us" className={`px-2 pt-1 border-b-2 ${isActive('about') ? 'text-white border-secondary font-semibold' : 'border-transparent hover:border-white text-white'}`}>
                About
              </a>

              {/* === Projects Mega Menu (Desktop) === */}
              <div className="relative group">
                <button className={`text-white px-3 pt-1 border-b-2 flex items-center gap-1 uppercase ${isActive('project') ? 'border-white' : 'border-transparent hover:border-white'}`}>
                  Projects <ChevronDown size={18} />
                </button>

                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4 hidden group-hover:block z-40">
                  <div className="w-[92vw] max-w-5xl normal-case tracking-normal font-normal text-base bg-white text-gray-800 shadow-2xl rounded-2xl border-t-4 border-secondary overflow-hidden">
                    <div className="grid grid-cols-3 divide-x divide-gray-100">
                      {/* Residential */}
                      <div className="p-6">
                        <DesktopColumnHeading title="Residential" url="/residential-projects" />
                        <div className="grid grid-cols-2 gap-5">
                          <div>
                            <Link to="/residential-projects/jaipur" className="block text-sm font-semibold text-secondary mb-2 hover:underline">
                              Jaipur
                            </Link>
                            <DesktopProjectList projects={residentialJaipur} loading={projectsLoading} />
                          </div>
                          <div>
                            <Link to="/residential-projects/mumbai" className="block text-sm font-semibold text-secondary mb-2 hover:underline">
                              Mumbai
                            </Link>
                            <DesktopProjectList projects={residentialMumbai} loading={projectsLoading} />
                          </div>
                        </div>
                      </div>

                      {/* Commercial */}
                      <div className="p-6">
                        <DesktopColumnHeading title="Commercial" url="/commercial-project" />
                        <DesktopProjectList projects={commercialProjects} loading={projectsLoading} />
                      </div>

                      {/* Industrial */}
                      <div className="p-6">
                        <DesktopColumnHeading title="Industrial" url="/industrial-project" />
                        <DesktopProjectList projects={industrialProjects} loading={projectsLoading} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <a href="/contact-us" className={`px-2 pt-1 border-b-2 ${isActive('connect') ? 'text-white border-secondary font-semibold' : 'border-transparent hover:border-white text-white'}`}>
                Connect
              </a>
            </nav>
            <div className="flex items-center gap-4">
              <button className="bg-secondary text-white px-10 py-2 rounded font-bold text-md uppercase" onClick={() => setModalOpen(true)}>Enquire</button>
              <div className="relative group">
                <button className="bg-white text-secondary px-10 py-2 rounded font-bold text-md uppercase flex items-center gap-1">
                  Login <ChevronDown size={18} />
                </button>
                <div className="absolute top-full right-0 pt-2 w-40 hidden group-hover:block">
                  <div className="bg-black/95 backdrop-blur-md shadow-lg rounded-md py-3 px-3">
                    {loginMenu.map((item) => item.subItems ? (
                      <div key={item.id} className="relative group/employee">
                        <div className="flex items-center justify-between px-4 py-2 text-sm rounded mb-1 text-white hover:bg-white/30 cursor-pointer">
                          <span>{item.label}</span>
                          <ChevronRight size={14} />
                        </div>
                        <div className="absolute right-full top-0 pr-1 w-32 hidden group-hover/employee:block">
                          <div className="bg-black/95 backdrop-blur-md shadow-lg rounded-md py-3 px-3">
                            {item.subItems.map((sub) => (
                              <a
                                key={sub.id}
                                href={sub.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => openExternalClean(e, sub.url)}
                                className="block px-4 py-2 text-sm rounded mb-1 text-white hover:bg-white/30"
                              >
                                {sub.label}
                              </a>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <a
                        key={item.id}
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => openExternalClean(e, item.url)}
                        className="block px-4 py-2 text-sm rounded mb-1 text-white hover:bg-white/30"
                      >
                        {item.label}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="md:hidden">
            <button onClick={() => setIsOpen(!isOpen)} aria-label="Toggle menu">
              {isOpen ? <X className="text-white h-6 w-6" /> : <Menu className="text-white h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* === Mobile Drawer === */}
        <div
          className={`fixed right-0 w-[70%] bg-black/90 backdrop-blur-sm shadow-md transform transition-transform duration-500 z-50 ${isOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
          style={{
            top: `${headerHeight}px`,
            height: `calc(100vh - ${headerHeight}px)`
          }}
        >
          <div className="flex flex-col h-full overflow-y-auto px-4 pt-6 pb-6 text-white text-sm font-medium uppercase">
            <a
              href="/about-us"
              onClick={closeMobileMenu}
              className={`block px-1 py-[14px] border-b border-white ${isActive('about') ? 'text-secondary' : 'text-white'}`}
            >
              About
            </a>

            {/* === Projects Accordion (Mobile) === */}
            <button
              onClick={() => setProjectOpen(!projectOpen)}
              className="w-full text-left px-1 py-[14px] border-b border-white flex justify-between items-center uppercase"
            >
              Projects
              <ChevronDown className={`${projectOpen ? 'rotate-180' : ''} transition-transform duration-300`} size={18} />
            </button>
            {projectOpen && (
            <div className="flex flex-col">
              {/* Residential */}
              <MobileAccordionRow
                label="Residential"
                url="/residential-projects"
                open={residentialOpen}
                onToggle={() => setResidentialOpen(!residentialOpen)}
                onNavigate={closeMobileMenu}
              >
                <MobileAccordionRow
                  label="Jaipur"
                  url="/residential-projects/jaipur"
                  depth={1}
                  open={jaipurOpen}
                  onToggle={() => setJaipurOpen(!jaipurOpen)}
                  onNavigate={closeMobileMenu}
                >
                  <MobileProjectList projects={residentialJaipur} onNavigate={closeMobileMenu} loading={projectsLoading} />
                </MobileAccordionRow>
                <MobileAccordionRow
                  label="Mumbai"
                  url="/residential-projects/mumbai"
                  depth={1}
                  open={mumbaiOpen}
                  onToggle={() => setMumbaiOpen(!mumbaiOpen)}
                  onNavigate={closeMobileMenu}
                >
                  <MobileProjectList projects={residentialMumbai} onNavigate={closeMobileMenu} loading={projectsLoading} />
                </MobileAccordionRow>
              </MobileAccordionRow>

              {/* Commercial */}
              <MobileAccordionRow
                label="Commercial"
                url="/commercial-project"
                open={commercialOpen}
                onToggle={() => setCommercialOpen(!commercialOpen)}
                onNavigate={closeMobileMenu}
              >
                <MobileProjectList projects={commercialProjects} onNavigate={closeMobileMenu} loading={projectsLoading} />
              </MobileAccordionRow>

              {/* Industrial */}
              <MobileAccordionRow
                label="Industrial"
                url="/industrial-project"
                open={industrialOpen}
                onToggle={() => setIndustrialOpen(!industrialOpen)}
                onNavigate={closeMobileMenu}
              >
                <MobileProjectList projects={industrialProjects} onNavigate={closeMobileMenu} loading={projectsLoading} />
              </MobileAccordionRow>
            </div>
            )}

            <a
              href="/contact-us"
              onClick={closeMobileMenu}
              className={`block px-1 py-[14px] border-b border-white ${isActive('connect') ? 'text-secondary' : 'text-white'}`}
            >
              Connect
            </a>

            <button
              onClick={() => {
                setModalOpen(true);
                setIsOpen(false);
              }}
              className=" bg-secondary text-white px-6 py-2 rounded-full font-semibold text-sm uppercase mt-8"
            >
              Enquire
            </button>
            <button
              onClick={() => setLoginOpen(!loginOpen)}
              className="w-full text-left px-1 py-[14px] border-b border-white flex justify-between items-center mt-4 uppercase"
            >
              Login
              <ChevronDown className={`${loginOpen ? 'rotate-180' : ''} transition-transform duration-300`} size={18} />
            </button>
            <div className={`flex flex-col overflow-hidden transition-all duration-500 ease-in-out ${loginOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
              <a
                href="https://server16.farvisioncloud.com/login"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  openExternalClean(e, "https://server16.farvisioncloud.com/login");
                  setIsOpen(false);
                }}
                className="py-[14px] pl-4 border-b border-white text-white"
              >
                Customer
              </a>
              <div className="flex items-center justify-between">
                <span className="flex-1 py-[14px] pl-4 border-b border-white text-white">Employee</span>
                <button
                  onClick={() => setEmployeeOpen(!employeeOpen)}
                  className="px-2 pt-[14px] pb-[17px] border-b border-white"
                >
                  <ChevronDown className={`${employeeOpen ? 'rotate-180' : ''} transition-transform duration-300`} size={16} />
                </button>
              </div>
              <div className={`flex flex-col overflow-hidden transition-all duration-500 ease-in-out ${employeeOpen ? 'max-h-32 opacity-100' : 'max-h-0 opacity-0'}`}>
                <a
                  href="https://mail.kgkrealty.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    openExternalClean(e, "https://mail.kgkrealty.com");
                    setIsOpen(false);
                  }}
                  className="py-[14px] pl-6 border-b border-white text-sm text-white"
                >
                  Email
                </a>
                <a
                  href="https://server16.farvisioncloud.com/login"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    openExternalClean(e, "https://server16.farvisioncloud.com/login");
                    setIsOpen(false);
                  }}
                  className="py-[14px] pl-6 border-b border-white text-sm text-white"
                >
                  Farvision
                </a>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* === Enquiry Modal === */}
      <EnquiryModalPopup  isOpen={modalOpen} onClose={() => setModalOpen(false)} />
      <FloatingActionButton />
    </>
  );
};

export default Navbar;
